import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Color } from 'ngx-color';
import { ColorChromeModule } from 'ngx-color/chrome';
import { ColorSketchModule } from 'ngx-color/sketch';
import tinycolor from 'tinycolor2';

export type ColorFormat = 'rgba' | 'hex';

@Component({
  selector: 'accredible-color-picker-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ColorSketchModule, ColorChromeModule, OverlayModule],
  templateUrl: './color-picker-input.component.html',
  styleUrls: ['./color-picker-input.component.scss'],
  // TODO(Fred): When using OnPush, the color-pallete.component is not updating the lighter and darker color pickers (even if using detectChanges())
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerInputComponent),
      multi: true,
    },
  ],
})
export class ColorPickerInputComponent implements OnInit, ControlValueAccessor {
  private readonly _destroyRef = inject(DestroyRef);

  label = input<string>();
  colorFormat = input<ColorFormat>('hex');
  swatches = model<string[]>([]); // adds a list of used colors for convenience
  disabledColorPicker = input(false);
  fullyColored = input(true);
  size = input<'small' | 'normal' | 'large'>('small');

  isDarkColor = signal(false);
  openColorPicker = signal(false);

  formControl = new FormControl('');

  /**
   * Positions the color picker to the top or bottom of the input,
   * the overlay will choose the best position depending on the content.
   */
  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'bottom',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
    },
  ];

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      this.onChange(value);
    });
  }

  toggleColorPicker(): void {
    this.openColorPicker.set(!this.openColorPicker());
  }

  /**
   * Updates value of input control with a new color from the color picker.
   * If palettes are set as independent - this update also triggers an event that bubbles up the formGroup updating
   * lighter and darker variants.
   * Also switches color from hex to rgba when alpha value is less than 1
   */
  onColorChange(color: Color): void {
    let colorString = color.hex;
    if (this.colorFormat() === 'rgba' || color.rgb.a < 1) {
      colorString = tinycolor(color.rgb).toRgbString();
    }
    this._setIsDarkColor(colorString);
    this.formControl.setValue(colorString);
  }

  onChange = (value: string): void => {
    this._setIsDarkColor(value);
  };

  onTouched = (): void => {
    // Do nothing
  };

  writeValue(value: string): void {
    this.formControl.setValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * If the perceived background color is dark, update input text color to white for better readability
   */
  private _setIsDarkColor(color: string): void {
    if (this.fullyColored()) {
      const isValid = tinycolor(color).isValid();
      const isTransparent = tinycolor(color).getAlpha() < 0.4 || color === 'transparent';

      if (!isValid || isTransparent) {
        this.isDarkColor.set(false);
      } else {
        this.isDarkColor.set(tinycolor(color).isDark());
      }
    } else {
      this.isDarkColor.set(false);
    }
  }
}
